import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { ppaUrls } from 'config/urls';
import { FilterContextData } from 'contexts/FilterContext';
import {
    getDefaultOptionLabel,
    getOptionLabel,
    getWhitespacePaginationLimits,
    getYKpiDataRangeFromControlsMeta,
    WHITESPACE_INITIAL_CHILD_VISIBLE_COUNT,
} from 'contexts/whitespaceAnalysisContext.helpers';
import {
    EMPTY_DROPDOWNS,
    EMPTY_GRID_REQUEST,
    EMPTY_OPTION_ITEMS,
    EMPTY_WHITESPACE_CONTEXT,
    type WhitespaceAnalysisContextProps,
    type WhitespaceAnalysisContextValue,
    type WhitespaceMetaResponse,
} from 'contexts/whitespaceAnalysisContext.types';
import useWhitespaceAnalysisGridData from 'hooks/useWhitespaceAnalysisGridData';
import { useWhitespaceYKpiConfigSync } from 'hooks/useWhitespaceYKpiConfigSync';
import React, { FC, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { AxisConfiguration } from 'components/molecules/PackArchitecture/AxisConfigurationModal';
import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/KpiAxisConfigurationModal';
import { mergeWhitespaceUserInputs } from 'components/pages/WhitespaceAnalysis/whitespaceGridRequest.helpers';
import { extractWhitespaceValueSalesLegendFromGridData } from 'components/pages/WhitespaceAnalysis/whitespaceGridKpiAxis.helpers';
import {
    buildWhitespaceXAxisGroupByControlsMeta,
    buildWhitespaceYAxisGroupByControlsMeta,
    buildWhitespaceYKpiControlsMeta,
    getWhitespaceYKpiBinLabelAffixes,
} from 'components/pages/WhitespaceAnalysis/whitespaceMeta.helpers';
import { useGetCurrencyForTenant } from 'hooks/general/useCurrencyForClient';
import { OPTION_ID_TYPE_KPI } from 'constants/ppa';

const WhitespaceAnalysisDataContext = createContext<WhitespaceAnalysisContextValue>(EMPTY_WHITESPACE_CONTEXT);

const WhitespaceAnalysisContext = ({ children }: WhitespaceAnalysisContextProps) => {
    const currencyPrefix = useGetCurrencyForTenant();
    const { timePeriodSelection, idForRestOfComponent, idForRestOfComponentLoading, filterNullError } =
        useContext(FilterContextData);
    const [xAxis, setXAxis] = useState('');
    const [yAxis, setYAxis] = useState('');
    const [crossSection, setCrossSection] = useState('');
    const [showWhitespaces, setShowWhitespaces] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [xAxisDimensionConfig, setXAxisDimensionConfig] = useState<AxisConfiguration | undefined>();
    const [yAxisKpiConfig, setYAxisKpiConfig] = useState<KpiAxisConfiguration | undefined>();
    const [yAxisDimensionConfig, setYAxisDimensionConfig] = useState<AxisConfiguration | undefined>();
    const [yAxisKpiDataRangeFromGrid, setYAxisKpiDataRangeFromGrid] = useState<
        WhitespaceAnalysisContextValue['yAxisKpiDataRange'] | undefined
    >();
    const [gridConfigApplyRevision, setGridConfigApplyRevision] = useState(0);
    const skipNextGridFetchRef = useRef(false);

    const { data: meta, isLoading: isMetaLoading } = useQuery<WhitespaceMetaResponse>({
        queryKey: ['whitespace-analysis-meta'],
        queryFn: async () => {
            const response = await axios.get(ppaUrls.getWhitespaceAnalysisMetaUrl);
            return response.data;
        },
        staleTime: 5 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
    const title = meta?.title ?? '';
    const tooltip = meta?.tooltip ?? '';
    const paginationLimits = useMemo(() => getWhitespacePaginationLimits(meta), [meta]);

    const dropdowns = useMemo(
        () => meta?.options?.drop_down ?? meta?.options?.dropdowns ?? EMPTY_DROPDOWNS,
        [meta?.options?.drop_down, meta?.options?.dropdowns],
    );
    const xAxisDropdown = useMemo(() => dropdowns.find((dropdown) => dropdown.id === 'x_axis'), [dropdowns]);
    const yAxisDropdown = useMemo(() => dropdowns.find((dropdown) => dropdown.id === 'y_axis'), [dropdowns]);
    const crossSectionDropdown = useMemo(() => dropdowns.find((dropdown) => dropdown.id === 'z_axis'), [dropdowns]);
    const xAxisOptionItems = useMemo(
        () => xAxisDropdown?.group_by_options ?? EMPTY_OPTION_ITEMS,
        [xAxisDropdown?.group_by_options],
    );
    // y_axis ships every selectable item (KPIs and attribute-buckets like
    // Pack_Range, config-driven via is_y_axis_attribute) in one `column_options`
    // array, tagged per-item via `id_type`. yAxisKpiOptionIds below stays
    // restricted to the `kpi_id` items so AxisConfigDropdown routes attribute
    // selections to the dimension settings modal, not the KPI one.
    const yAxisOptionItems = useMemo(
        () => yAxisDropdown?.column_options ?? EMPTY_OPTION_ITEMS,
        [yAxisDropdown?.column_options],
    );
    const yAxisKpiOptionItems = useMemo(
        () => yAxisOptionItems.filter((option) => option.id_type === OPTION_ID_TYPE_KPI),
        [yAxisOptionItems],
    );
    const crossSectionOptionItems = useMemo(
        () => crossSectionDropdown?.column_options ?? EMPTY_OPTION_ITEMS,
        [crossSectionDropdown?.column_options],
    );

    const xAxisOptions = useMemo(() => xAxisOptionItems.map(getOptionLabel).filter(Boolean), [xAxisOptionItems]);
    const yAxisOptions = useMemo(() => yAxisOptionItems.map(getOptionLabel).filter(Boolean), [yAxisOptionItems]);
    const crossSectionOptions = useMemo(
        () => crossSectionOptionItems.map(getOptionLabel).filter(Boolean),
        [crossSectionOptionItems],
    );
    const yAxisKpiOptionIds = useMemo(
        () => yAxisKpiOptionItems.map(getOptionLabel).filter(Boolean),
        [yAxisKpiOptionItems],
    );

    const selectedYAxisOption = useMemo(
        () => yAxisOptionItems.find((option) => getOptionLabel(option) === yAxis),
        [yAxis, yAxisOptionItems],
    );
    const effectiveYAxisOption = useMemo(
        () => selectedYAxisOption ?? yAxisOptionItems.find((option) => option.is_default) ?? yAxisOptionItems[0],
        [selectedYAxisOption, yAxisOptionItems],
    );
    const isYAxisAttribute = Boolean(effectiveYAxisOption?.is_attribute);
    const defaultYAxisKpiItem = useMemo(
        () => yAxisKpiOptionItems.find((option) => option.is_default) ?? yAxisKpiOptionItems[0],
        [yAxisKpiOptionItems],
    );
    const selectedXAxisOption = useMemo(
        () => xAxisOptionItems.find((option) => getOptionLabel(option) === xAxis),
        [xAxis, xAxisOptionItems],
    );
    const selectedCrossSectionOption = useMemo(
        () => crossSectionOptionItems.find((option) => getOptionLabel(option) === crossSection),
        [crossSection, crossSectionOptionItems],
    );

    // When Pack_Range (or any is_y_axis_attribute option) is selected, the Y
    // option id is the attribute type, not a KPI - prefer its configured
    // companion `options` entry of id_type `kpi_id` (e.g. Size_Range ->
    // ws_pack_size_rank, for an "avg pack size" secondary cell metric); fall
    // back to the default KPI (mirrors the backend's own `_default_bucket_kpi`)
    // when no companion kpi_id is configured, so cell value formatting/
    // coloring still has a KPI to key off of.
    const attributeCompanionKpiId = effectiveYAxisOption?.options?.find(
        (option) => option.id_type === OPTION_ID_TYPE_KPI,
    )?.id;
    const selectedYAxisKpiId = isYAxisAttribute
        ? attributeCompanionKpiId ?? defaultYAxisKpiItem?.id ?? ''
        : effectiveYAxisOption?.id ?? '';
    const selectedXAxisDimension = selectedXAxisOption?.dimension ?? '';
    const selectedXAxisAttributeType = selectedXAxisOption?.id ?? '';
    const selectedYAxisAttributeType = isYAxisAttribute ? effectiveYAxisOption?.id ?? '' : '';
    const selectedYAxisDimension = isYAxisAttribute ? effectiveYAxisOption?.dimension ?? '' : '';
    const selectedCrossSectionAttributeType = selectedCrossSectionOption?.id ?? '';
    const selectedCrossSectionDimension = selectedCrossSectionOption?.dimension ?? '';

    const canBuildGridRequest = Boolean(
        selectedXAxisDimension &&
            selectedXAxisAttributeType &&
            selectedYAxisKpiId &&
            selectedCrossSectionAttributeType &&
            selectedCrossSectionDimension &&
            (!isYAxisAttribute || (selectedYAxisAttributeType && selectedYAxisDimension)),
    );

    const xAxisGroupByControlsMeta = useMemo(
        () =>
            buildWhitespaceXAxisGroupByControlsMeta(
                xAxisDropdown,
                xAxis,
                selectedXAxisOption?.id ?? xAxis,
            ),
        [selectedXAxisOption?.id, xAxis, xAxisDropdown],
    );

    const yAxisGroupByControlsMeta = useMemo(
        () =>
            buildWhitespaceYAxisGroupByControlsMeta(
                yAxisDropdown,
                yAxis,
                effectiveYAxisOption?.id ?? yAxis,
                effectiveYAxisOption?.id_type || 'attribute_type',
            ),
        [effectiveYAxisOption?.id, effectiveYAxisOption?.id_type, yAxis, yAxisDropdown],
    );

    const yAxisKpiControlsMeta = useMemo(
        () => buildWhitespaceYKpiControlsMeta(yAxisDropdown, OPTION_ID_TYPE_KPI),
        [yAxisDropdown],
    );

    const yAxisKpiDataRangeFromMeta = useMemo(
        () => getYKpiDataRangeFromControlsMeta(yAxisKpiControlsMeta),
        [yAxisKpiControlsMeta],
    );

    const yAxisKpiDataRange = yAxisKpiDataRangeFromGrid ?? yAxisKpiDataRangeFromMeta;

    const yAxisKpiBinLabelAffixes = useMemo(
        () => getWhitespaceYKpiBinLabelAffixes(effectiveYAxisOption, currencyPrefix),
        [currencyPrefix, effectiveYAxisOption],
    );
    const showNoFilterSelection = isInitialized && !idForRestOfComponentLoading && Boolean(filterNullError);

    const toggleConfig = meta?.options?.toggle?.[0];
    const toggleInput = toggleConfig?.input_list?.[0];
    const toggleTooltip = toggleInput?.tooltip ?? '';
    const whitespaceToggleDefault = toggleConfig?.default_enabled ?? toggleInput?.default_value ?? false;

    useEffect(() => {
        if (!meta || isInitialized) return;
        setXAxis(getDefaultOptionLabel(xAxisOptionItems));
        setYAxis(getDefaultOptionLabel(yAxisOptionItems));
        setCrossSection(getDefaultOptionLabel(crossSectionOptionItems));
        setShowWhitespaces(Boolean(whitespaceToggleDefault));
        setIsInitialized(true);
    }, [
        crossSectionOptionItems,
        isInitialized,
        meta,
        whitespaceToggleDefault,
        xAxisOptionItems,
        yAxisOptionItems,
    ]);

    useEffect(() => {
        setXAxisDimensionConfig(undefined);
    }, [xAxis]);

    useEffect(() => {
        setYAxisDimensionConfig(undefined);
    }, [yAxis]);

    // The Y-axis min/max slider bounds are data-derived (from the grid response),
    // so they must be re-derived whenever the underlying data changes: on Y-axis
    // KPI change, X-axis dimension change, or global filter change. Without this,
    // yAxisKpiDataRangeFromGrid stays "sticky" (see useWhitespaceYKpiConfigSync,
    // which only populates it once) and silently uses a stale range.
    useEffect(() => {
        setYAxisKpiConfig(undefined);
        setYAxisKpiDataRangeFromGrid(undefined);
        setGridConfigApplyRevision(0);
    }, [yAxis, xAxis, crossSection, idForRestOfComponent?.dim1?.ids, idForRestOfComponent?.dim2?.ids]);

    const applyXAxisDimensionConfig = useCallback((config: AxisConfiguration) => {
        setXAxisDimensionConfig(config);
        setGridConfigApplyRevision((revision) => revision + 1);
    }, []);

    const applyYAxisKpiConfig = useCallback((config: KpiAxisConfiguration) => {
        setYAxisKpiConfig(config);
        setGridConfigApplyRevision((revision) => revision + 1);
    }, []);

    const applyYAxisDimensionConfig = useCallback((config: AxisConfiguration) => {
        setYAxisDimensionConfig(config);
        setGridConfigApplyRevision((revision) => revision + 1);
    }, []);

    const parsedTimePeriod = useMemo(() => JSON.parse(timePeriodSelection ?? '{}'), [timePeriodSelection]);
    const startTimestamp = parsedTimePeriod?.start_timestamp;
    const endTimestamp = parsedTimePeriod?.end_timestamp;

    const gridRequestPayload = useMemo(() => {
        if (!canBuildGridRequest) {
            return EMPTY_GRID_REQUEST;
        }

        return {
            kpi_ids: [selectedYAxisKpiId],
            time_period: {
                start_timestamp: startTimestamp,
                end_timestamp: endTimestamp,
            },
            filters: {
                dim1_id_list: idForRestOfComponent?.dim1?.ids ?? [],
                dim2_id_list: idForRestOfComponent?.dim2?.ids ?? [],
            },
            aggregates: [
                {
                    dimension: selectedXAxisDimension,
                    attribute_type: selectedXAxisAttributeType,
                    is_attribute: Boolean(selectedXAxisOption?.is_attribute),
                    is_cross_section: false,
                },
                {
                    dimension: selectedCrossSectionDimension,
                    attribute_type: selectedCrossSectionAttributeType,
                    is_attribute: Boolean(selectedCrossSectionOption?.is_attribute),
                    is_cross_section: true,
                },
            ],
            ...(isYAxisAttribute
                ? {
                      bucket_aggregate: {
                          dimension: selectedYAxisDimension,
                          attribute_type: selectedYAxisAttributeType,
                          is_attribute: true,
                          is_cross_section: false,
                      },
                  }
                : {}),
            user_inputs: mergeWhitespaceUserInputs({
                xAxisDimensionConfig,
                yAxisKpiConfig,
                yAxisDimensionConfig,
                xAxisGroupByControlsMeta,
                yAxisKpiControlsMeta,
                yAxisGroupByControlsMeta,
                yAxisKpiDataRange: yAxisKpiDataRange,
                isYAxisAttribute,
            }),
            pagination: {
                child: {
                    limit: WHITESPACE_INITIAL_CHILD_VISIBLE_COUNT,
                    offset: 0,
                },
                column: {
                    limit:
                        xAxisDimensionConfig?.showTop && xAxisDimensionConfig.showTop > 0
                            ? xAxisDimensionConfig.showTop
                            : paginationLimits.column,
                    offset: 0,
                },
                row: {
                    limit:
                        isYAxisAttribute && yAxisDimensionConfig?.showTop && yAxisDimensionConfig.showTop > 0
                            ? yAxisDimensionConfig.showTop
                            : paginationLimits.row,
                    offset: 0,
                },
            },
        };
    }, [
        canBuildGridRequest,
        endTimestamp,
        idForRestOfComponent?.dim1?.ids,
        idForRestOfComponent?.dim2?.ids,
        isYAxisAttribute,
        selectedCrossSectionAttributeType,
        selectedCrossSectionDimension,
        selectedCrossSectionOption?.is_attribute,
        selectedXAxisAttributeType,
        selectedXAxisDimension,
        selectedXAxisOption?.is_attribute,
        selectedYAxisAttributeType,
        selectedYAxisDimension,
        selectedYAxisKpiId,
        startTimestamp,
        xAxisDimensionConfig,
        xAxisGroupByControlsMeta,
        yAxisDimensionConfig,
        yAxisGroupByControlsMeta,
        yAxisKpiConfig,
        yAxisKpiControlsMeta,
        yAxisKpiDataRange,
        paginationLimits.column,
        paginationLimits.row,
    ]);

    const {
        columnHeaders,
        gridRows,
        initialGridResponse,
        isGridLoading,
        isGridFetching,
        hasGridError,
        isGridFetched,
        loadMoreRows,
        loadMoreColumns,
        hasMoreRows,
        hasMoreColumns,
        isFetchingMoreRows,
        isFetchingMoreColumns,
        totalColumnCount,
        totalRowCount,
    } = useWhitespaceAnalysisGridData({
        gridRequestPayload,
        yAxis,
        xAxis,
        crossSection,
        startTimestamp,
        endTimestamp,
        idForRestOfComponentLoading,
        canFetch: canBuildGridRequest,
        skipNextGridFetchRef,
        gridConfigApplyRevision,
    });

    useWhitespaceYKpiConfigSync({
        initialGridResponse,
        isGridFetching,
        yAxisKpiControlsMeta,
        yAxisKpiConfig,
        setYAxisKpiConfig,
        yAxisKpiDataRangeFromGrid,
        setYAxisKpiDataRangeFromGrid,
        yAxisKpiDataRangeFromMeta,
        yAxisKpiDataRange,
        skipNextGridFetchRef,
        isYAxisAttribute,
    });

    const valueSalesLegend = useMemo(
        () => extractWhitespaceValueSalesLegendFromGridData(initialGridResponse),
        [initialGridResponse],
    );

    return (
        <WhitespaceAnalysisDataContext.Provider
            value={{
                title,
                tooltip,
                whitespaceToggleTooltip: toggleTooltip,
                showNoFilterSelection,
                xAxis,
                setXAxis,
                yAxis,
                setYAxis,
                selectedYAxisKpiId,
                crossSection,
                setCrossSection,
                showWhitespaces,
                setShowWhitespaces,
                xAxisOptions,
                yAxisOptions,
                crossSectionOptions,
                xAxisOptionItems,
                yAxisOptionItems,
                xAxisFilter: {
                    dimension: selectedXAxisDimension,
                    attributeType: selectedXAxisAttributeType,
                    isAttribute: Boolean(selectedXAxisOption?.is_attribute),
                },
                childLimit: paginationLimits.child,
                gridRequestPayload,
                columnHeaders,
                gridRows,
                loadMoreRows,
                loadMoreColumns,
                hasMoreRows,
                hasMoreColumns,
                isFetchingMoreRows,
                isFetchingMoreColumns,
                isMetaLoading,
                isGridLoading,
                isGridFetching,
                hasGridError,
                isGridFetched,
                totalColumnCount,
                totalRowCount,
                xAxisDimensionConfig,
                setXAxisDimensionConfig,
                applyXAxisDimensionConfig,
                yAxisKpiConfig,
                setYAxisKpiConfig,
                applyYAxisKpiConfig,
                yAxisDimensionConfig,
                setYAxisDimensionConfig,
                applyYAxisDimensionConfig,
                isYAxisAttribute,
                xAxisGroupByControlsMeta,
                yAxisGroupByControlsMeta,
                yAxisKpiControlsMeta,
                yAxisKpiDataRange,
                yAxisKpiBinLabelAffixes,
                yAxisKpiOptionIds,
                valueSalesLegend,
            }}
        >
            {children}
        </WhitespaceAnalysisDataContext.Provider>
    );
};

const useWhitespaceAnalysisContext = () => useContext(WhitespaceAnalysisDataContext);

const WhitespaceAnalysisContextWrapper: FC<WhitespaceAnalysisContextProps> = ({ children }) => {
    return <WhitespaceAnalysisContext>{children}</WhitespaceAnalysisContext>;
};

export { WhitespaceAnalysisContext, WhitespaceAnalysisContextWrapper, useWhitespaceAnalysisContext };
